import type {
	LightningReportDataset,
	LightningReportErrorEllipseConfidence,
	LightningReportScheduleDayOfWeek,
	LightningReportScheduleFrequency,
} from '@xweather/protect-types'
import type { Severity } from './data/alerts/types.ts'
import type { LayerGroupId, LayerViewId } from './features/layers/layers.tsx'
import type { DurationPreset } from './features/reports/create-form/lightning/fields/LightningTimeSelection.tsx'
import type { LightningReportForm } from './features/reports/create-form/lightning/schema.tsx'
import type { StrikeDamagePotentialOption } from './features/reports/display-form/lightning/lightningReportUtils.ts'
import type { ReportView } from './features/reports/display-form/reportViews.ts'

export const ids = {
	common: {
		loading: 'pageLoading',
		topBarBackBtn: 'topBarBackBtn',
		helpBtn: 'helpBtn',
	},
	confirmDialog: {
		root: 'confirmDialogRoot',
		title: 'confirmDialogTitle',
		content: 'confirmDialogContent',
		confirmBtn: 'confirmDialogConfirmBtn',
		cancelBtn: 'confirmDialogCancelBtn',
	},
	navigationBar: {
		hamburgerMenuBtn: 'navHamburgerBtn',
		measurements: 'navMeasurements',
		map: 'navMap',
		dashboard: 'navDashboard',
		avatarBtn: 'navAvatarBtn',
		xweatherLogo: 'navXweatherLogo',
		hamburgerMenuUsers: 'navHamburgerUsers',
		hamburgerMenuApiKeys: 'navHamburgerApiKeys',
		hamburgerMenuApiDocs: 'navHamburgerApiDocs',
		hamburgerMenuDevices: 'navHamburgerDevices',
		hamburgerMenuHelpCenter: 'navHamburgerHelpCenter',
		hamburgerMenuSupport: 'navHamburgerSupport',
		hamburgerMenuSubscription: 'navHamburgerSubscription',
		hamburgerMenuServiceMap: 'navHamburgerServiceMap',
		hamburgerMenuAccounts: 'navHamburgerAccounts',
		hamburgerMenuWebRelays: 'navHamburgerWebRelays',
		hamburgerMenuWebhooks: 'navHamburgerWebhooks',
		avatarMenuPreferences: 'navAvatarPreferences',
		avatarMenuLogout: 'navAvatarLogout',
		alerts: 'navAlerts',
		reports: 'navReports',
	},
	apiKeys: {
		addApiKeyBtn: 'apiKeysAddApiKey',
		deleteAPiKeyDialogTitle: 'apiKeysDeleteApiKeyDialogTitle',
		deleteAPiKeyDialogContent: 'apiKeysDeleteApiKeyDialogContent',
		deleteApiKeyDialogCancel: 'apiKeysDeleteApiKeyDialogCancel',
		deleteApiKeyDialogDelete: 'apiKeysDeleteApiKeyDialogDelete',
		copyApiKeyButton: 'apiKeysCopyApiKeyButton',
		blockApiKeyButton: 'apiKeysBlockApiKeyButton',
		activateApiKeyButton: 'apiKeysActivateApiKeyButton',
		deleteApiKeyButton: 'apiKeysDeleteApiKeyButton',
		editApiKeyNameSave: 'apiKeysEditApiKeyNameSave',
		editApiKeyNameCancel: 'apiKeysEditApiKeyNameCancel',
		editApiKeyName: 'apiKeysEditApiKeyName',
	},
	users: {
		userListCount: 'usersUserListCount',
		penEditBtn: 'usersPenEditBtn',
		addUserRoleSelect: (index: number, label?: string) =>
			label ? `usersAddUserRoleSelect-${index}-${label}` : `usersAddUserRoleSelect-${index}`,
		addUserRoleSelectOption: (role: string) => `usersAddUserRoleSelectOption-${role}`,
		addUserBtn: 'usersAddUserBtn',
		editUserListBtn: 'editUserListBtn',
		newUserListBtn: 'newUserListBtn',
		addUserRow: 'usersAddUserRow',
		deleteUserRow: (index: number) => `usersDeleteUserRow-${index}`,
		addUserEmail: (index: number, label?: string) => (label ? `usersAddUserEmail-${index}-${label}` : `usersAddUserEmail-${index}`),
		addUserEmailInput: (index: number) => `usersAddUserEmailInput-${index}`,
		addUserName: (index: number, label?: string) => (label ? `usersAddUserName-${index}-${label}` : `usersAddUserName-${index}`),
		addUserPhone: (index: number, label?: string) => (label ? `usersAddUserPhone-${index}-${label}` : `usersAddUserPhone-${index}`),
		addUserBlock: (index: number) => `usersAddUserBlock-${index}`,
		addUserNameInput: 'usersAddUserNameInput',
		tableIcon: 'usersTableIcon',
		tableName: 'usersTableName',
		tableEmail: 'usersTableEmail',
		tablePhone: 'usersTablePhone',
		tableStatus: 'usersTableStatus',
		tableRole: 'usersTableRole',
		tableCreated: 'usersTableCreated',
		addUserDropdownArrow: 'arrowDropDownIcon',
		threeDotMenu: {
			btn: (id: string) => `usersThreeDotsBtn-${id}`,
			resetPassword: 'usersThreeDotMenuResetPassword',
			resendEmail: 'usersThreeDotMenuResendEmail',
			enableMFA: 'usersThreeDotMenuEnableMfa',
			disableMFA: 'usersThreeDotMenuDisableMfa',
			blockOption: 'usersThreeDotMenuBlockOption',
			delete: 'usersThreeDotMenuDelete',
			copyApiKey: 'usersThreeDotMenuCopyApiKey',
		},
		threeDotMenuResetPassword: 'usersThreeDotMenuResetPassword',
		threeDotMenuResendEmail: 'usersThreeDotMenuResendEmail',
		threeDotMenuEnableMFA: 'usersThreeDotMenuEnableMfa',
		threeDotMenuDisableMFA: 'usersThreeDotMenuDisableMfa',
		threeDotMenuBlockOption: 'usersThreeDotMenuBlockOption',
		threeDotMenuDelete: 'usersThreeDotMenuDelete',
		threeDotMenuCopyApiKey: 'usersThreeDotMenuCopyApiKey',
		search: {
			input: 'usersSearchInput',
			xBtn: 'usersSearchXBtn',
		},
		userList: {
			listName: 'usersListListName',
			userName: (email: string) => `userName-${email}`,
			userStatus: (email: string) => `userStatus-${email}`,
			userEmail: (email: string) => `userEmail-${email}`,
			filterInput: 'usersListFilterInput',
			setDefault: 'usersListSetDefault',
			userSelect: (email: string) => `usersListUserSelect-${email}`,
			reviewListName: 'usersListReviewListName',
			reviewCount: 'usersListReviewCount',
		},
	},
	map: {
		map: 'map',
		myLocationBtn: 'mapMyLocation',
		zoomInBtn: 'mapZoomIn',
		zoomOutBtn: 'mapZoomOut',
		// TODO: remove styleTheme and styleSatellite when Timeline / Legend integrated in mobile
		styleTheme: 'mapStyleTheme',
		styleSatellite: 'mapStyleSatellite',
		baseStyle: { button: (style: 'light' | 'dark' | 'satellite') => `mapStyleButton-${style}` },
		severeLayers: 'mapSevereLayers',
		settingsBtn: 'mapSettingsBtn',
		doneBtn: 'mapDoneBtn',
		legendBtn: 'mapLegendBtn',
		locationAndScaleBox: { location: 'mapLocation', scale: 'mapScale' },
		locationList: {
			searchBtn: 'mapLocationListSearchBtn',
			searchInput: 'mapLocationListSearchInput',
			topBtn: 'mapLocationListTopBtn',
			table: 'mapLocationListTable',
			alerts: (locationId: string) => `mapLocationListAlerts-${locationId}`,
			starCheckbox: 'mapLocationListStarCheckbox',
			silenceBtn: (locationId: string) => `mapLocationListSilenceBtn-${locationId}`,
		},
		layers: {
			closeBtn: 'mapLayersClose',
			group: (id: LayerGroupId) => `mapLayersGroup-${id}`,
			view: (id: LayerViewId) => `mapLayersView-${id}`,
			toggleButton: (id: LayerViewId) => `mapLayerToggleButton-${id}`,
			optionSwitch: (id: LayerGroupId) => `mapLayerOptionSwitch-${id}`,
		},
		mapMarker: {
			marker: (locationId: string) => `mapMarker-${locationId}`,
			markerAlerts: 'mapMarkerAlerts',
			coordinateMarker: { root: 'mapCoordinateMarker', latitude: 'mapCoordinateMarkerLatitude', longitude: 'mapCoordinateMarkerLongitude' },
			selectedCoordinateMarker: 'mapSelectedCoordinateMarker',
		},
		timeline: {
			playPauseBtn: 'mapTimelinePlayPauseBtn',
			periodBtn: 'mapTimelinePeriodBtn',
			timeDisplay: 'mapTimelineTimeDisplay',
			timePeriod: { popover: 'timePeriodPopover' },
			playbackSpeed: 'mapTimelinePlaybackSpeed',
		},
		panel: {
			root: 'mapPanelRoot',
			heading: 'mapPanelHeading',
			alertPanel: {
				edit: 'mapPanelEdit',
				download: {
					btn: 'mapPanelDownloadAlertsCSV',
					option: (type: 'lightning' | 'hail' | 'wind' | 'all') => `mapPanelDownloadAlertsCSV-${type}`,
				},
				card: 'mapPanelAlertCard',
				category: (type: 'high' | 'low') => `mapPanelAlertCardCategory-${type}`,
				assetDetail: { row: 'mapPanelAssetDetail' },
				lat: 'mapPanelAssetDetailLatitude',
				lon: 'mapPanelAssetDetailLongitude',
				value: (topic: string) => `mapPanelAlertDetailValue-${topic}`,
				silenceBtn: (vrn: string) => `mapPanelAlertCardSilenceBtn-${vrn}`,
			},
		},
		mapDraw: {
			drawControlAddBtn: 'mapDrawAddBtn',
			drawControlDeleteBtn: 'mapDrawDeleteBtn',
		},
	},
	devices: {
		addDeviceBtn: 'devicesAddDeviceBtn',
		totalCount: 'devicesTotalCount',
		table: {
			status: 'deviceTableStatus',
			name: 'devicesTableName',
			lastReported: 'devicesTableLastReported',
			device: 'devicesTableDevice',
			components: 'devicesTableComponents',
			location: 'devicesTableLocation',
			lastConnected: 'devicesTableLastConnected',
		},
		details: {
			heading: 'deviceDetailsHeading',
			status: 'deviceDetailsStatus',
			device: 'deviceDetailsModel',
			id: 'deviceDetailsId',
			lastConnected: 'deviceDetailsLastConnected',
			serialNumber: 'deviceDetailsSerialNumber',
			firmware: 'deviceDetailsFirmware',
			accumulationResetTime: 'deviceDetailsAccumulationResetTime',
			location: { latitude: 'deviceDetailsLatitude', longitude: 'deviceDetailsLongitude', altitude: 'deviceDetailsAltitude' },
			copyBtn: 'deviceDetailsCopyBtn',
			threeDotBtn: 'deviceDetailsThreeDotBtn',
			actionMenu: { restartOption: 'deviceDetailsActionMenuRestart', deleteOption: 'deviceDetailsActionMenuDelete' },
			deleteConfirm: 'deleteConfirm',
			deleteConfirmCancel: 'deleteConfirmCancel',
			deleteConfirmTitle: 'deleteConfirmTitle',
			deleteConfirmText: 'deleteConfirmText',
			penEditBtn: 'deviceDetailsPenEditBtn',
			viewMeasurementsBtn: 'deviceDetailsViewMeasurementsBtn',
			linearCorrection: { toggle: 'deviceDetailsLinearCorrectionToggle' },
		},
		form: {
			deviceType: 'deviceFormDeviceType',
			serialNumber: 'deviceFormSerialNumber',
			displayName: 'deviceFormDisplayName',
			location: {
				latitude: 'deviceFormLocationLatitude',
				longitude: 'deviceFormLocationLongitude',
				altitude: 'deviceFormLocationAltitude',
			},
			accumulationResetTime: 'deviceFormAccumulationResetTime',
		},
	},
	formLayout: {
		heading: 'formLayoutHeading',
		saveBtn: 'formLayoutSaveBtn',
		cancelBtn: 'formLayoutCancelBtn',
		nextBtn: 'formLayoutNextBtn',
		backBtn: 'formLayoutBackBtn',
		deleteBtn: 'formLayoutDeleteBtn',
		collapsePanelBtn: 'formLayoutCollapsePanelBtn',
		expandPanelBtn: 'formLayoutExpandPanelBtn',
		step: (index: number) => {
			if (index <= 0) throw new Error(`Step index must be greater than 0, received: ${index}`)
			return `formLayoutStep-${index}`
		},
		minusBtn: 'formLayoutMinusBtn',
		plusBtn: 'formLayoutPlusBtn',
	},
	measurements: {
		alertTooltip: (parameter: string, statistic: string, iconType: 'ABOVE' | 'BELOW') =>
			`measurement-tooltip-${parameter}-${statistic}-${iconType}`,
		table: {
			name: 'measurementsTableName',
			lastReported: 'measurementsTableLastReported',
			measurementCol: (deviceId: string) => `measurement-col-${deviceId}`,
			alertArrow: (parameter: string, statistic: string) => `measurementAlertArrow-${parameter}-${statistic}`,
		},
		measurementReportsLink: 'measurementsReportsLink',
		viewDevicesLink: 'measurementsViewDevicesLink',
		optionsBtn: 'measurementsOptionsBtn',
		csv: {
			devices: 'devices',
			deviceListOption: (deviceId: string) => `deviceListOption-${deviceId}`,
			selectedDeviceOption: (deviceId: string) => `selectedDeviceOption-${deviceId}`,
			sensors: 'sensors',
			sensorListOption: (sensor: string) => `sensorListOption-${sensor}`,
			sensorOption: (sensor: string) => `sensorOption-${sensor}`,
			datePicker: {
				dateRangePickerStart: 'dateRangePickerStart',
				dateRangePickerEnd: 'dateRangePickerEnd',
				input: 'dateRangePickerInput',
			},
			intervalToggleButton: (option: string) => `intervalToggleButton-${option}`,
			qualityToggleButton: (option: string) => `qualityToggleButton-${option}`,
			backButton: 'backButton',
			downloadButton: 'downloadButton',
		},
		clock: {
			root: 'measurementsClock',
			timezone: 'measurementsClockTimezone',
			time: 'measurementsClockTime',
			tooltip: {
				userTime: 'measurementsClockTooltipUserTime',
				systemTime: 'measurementsClockTooltipSystemTime',
				utcTime: 'measurementsClockTooltipUtcTime',
			},
		},
	},
	preferences: {
		close: 'preferencesCloseBtn',
		logout: 'preferencesLogoutBtn',
		save: 'preferencesSaveBtn',
		cancel: 'preferencesCancelBtn',
		profile: {
			main: 'preferencesProfile',
			name: 'preferencesProfileName',
			role: 'preferencesProfileRole',
			phone: 'preferencesProfilePhone',
			code: 'preferencesProfilePhoneVerificationCode',
			verifyPhoneBtn: 'preferencesProfileVerifyPhoneBtn',
			cancelVerifyBtn: 'preferencesProfileCancelPhoneVerificationBtn',
			removePhoneBtn: 'preferencesProfileRemovePhoneBtn',
		},
		notifications: {
			main: 'preferencesNotifications',
			browser: 'preferencesNotificationsBrowser',
			browserLightningRings: 'preferencesNotificationsBrowserLightningRings',
			email: 'preferencesNotificationsEmail',
			sms: 'preferencesNotificationsSms',
			snooze: {
				main: 'preferencesNotificationsSnooze',
				switch: 'preferencesNotificationsSnoozeSwitch',
				start: 'preferencesNotificationsSnoozeStart',
				end: 'preferencesNotificationsSnoozeEnd',
			},
		},
		units: {
			main: 'preferencesUnits',
		},
		regionalPreferences: {
			main: 'preferencesRegionalPreferences',
			format: {
				dropDown: 'preferencesDateFormat',
				option: (format: 'DD.MM.YYYY' | 'MM.DD.YYYY') => `preferencesDateFormat-${format}`,
			},
			language: { main: 'preferencesLanguage', option: (lang: string) => `preferencesLanguage-${lang}` },
			timezone: {
				main: 'preferencesTimezone',
				dropDown: 'preferencesTimezoneDropDown',
				listboxPopup: 'preferencesTimezoneListboxPopup',
				autodetect: 'preferencesAutodetect',
			},
		},
		airQualityIndex: {
			main: 'preferencesAirQualityIndex',
		},
		appearance: {
			main: 'preferencesAppearance',
			option: (opt: 'LIGHT' | 'DARK' | 'SYSTEM') => `preferencesUnits-${opt.toLocaleUpperCase()}`,
		},
	},
	reports: {
		form: {
			root: 'reportsForm',
			reportType: 'reportsFormReportType',
			mapSelection: {
				selectedCount: 'reportsFormMapSelectedCount',
				clearAll: 'reportsFormMapClearAll',
				clickMode: 'reportsFormMapClickMode',
				dragMode: 'reportsFormMapDragMode',
				hint: 'reportsFormMapHint',
				dragRectangle: 'reportsFormMapDragRectangle',
			},
			areaOfInterest: {
				location: 'reportsFormAreaOfInterestLocation',
				search: 'reportsFormAreaOfInterestSearch',
				searchXBtn: 'reportsFormAreaOfInterestSearchXbtn',
				radius: 'reportsFormRadius',
				assetType: 'reportsFormAssetType',
				customPointToggle: 'reportsFormCustomPointToggle',
				customPointCoordinate: (input: 'latitude' | 'longitude') => `reportsFormCustomPoint-${input}`,
			},
			timeRange: {
				localTimeBtn: 'reportsFormLocalTimeBtn',
				timezone: 'reportsFormTimezone',
				start: 'reportsFormTimeRangeStart',
				end: 'reportsFormTimeRangeEnd',
				timeField: 'reportsFormTimeField',
			},
			reportDetails: {
				name: 'reportsFormReportDetailsName',
			},
			lightning: {
				dataset: (dataset: LightningReportDataset) => `reportsFormDataset-${dataset}`,
				timeRange: {
					duration: ({ amount, unit }: DurationPreset) => `reportsFormDuration-${amount}${unit}`,
					moreBtn: 'reportsFormDurationMore',
					msField: 'reportsFormTimeRangeMsField',
					faultTimeEntry: {
						toggle: 'reportsFormFaultTimeEntryToggle',
						dateTimeField: 'reportsFormFaultTimeEntryDateTime',
						plusMinusIcon: 'reportsFormFaultTimeEntryPlusMinusIcon',
						buffer: {
							select: 'reportsFormFaultTimeEntryBufferSelect',
							amount: (amount: number) => `reportsFormFaultTimeEntryBufferAmount-${amount}`,
							unit: (unit: 'second' | 'minute' | 'hour') => `reportsFormFaultTimeEntryBufferUnit-${unit}`,
						},
					},
					repeatedSchedule: {
						repetitionSelect: 'reportsFormRepetitionSelect',
						repetitionOption: (option: LightningReportForm['timing']['type']) => `reportsFormRepetitionOption-${option}`,
						period: (frequency: LightningReportScheduleFrequency) => `reportsFormRepeatedSchedulePeriod-${frequency}`,
						weekday: (weekday: LightningReportScheduleDayOfWeek) => `reportsFormRepeatedScheduleWeekday-${weekday}`,
						startScheduleAt: 'reportsFormRepeatedScheduleStartAt',
						stopScheduleAt: 'reportsFormRepeatedScheduleStopAt',
					},
				},
				reportDetails: {
					errorEllipsesToggle: 'reportsFormErrorEllipsesEnabled',
					errorEllipseConfidenceSelection: (confidence: LightningReportErrorEllipseConfidence) =>
						`reportsFormErrorEllipseConfidence-${confidence}`,
					strikeDamagePotentialToggle: 'reportsFormStrikeDamagePotentialToggle',
					cloudToCloudEventsToggle: 'reportsFormCloudToCloudEventsToggle',
					signalStrengthFilter: (type: 'all' | 'positive' | 'custom') => `reportsFormSignalStrengthFilter-${type}`,
					customSignalStrengthSlider: {
						root: 'reportsFormCustomSignalStrengthSlider',
					},
				},
			},
		},
		displayReportSettings: {
			showErrorEllipses: 'reportsDisplayReportSettingsShowErrorEllipses',
			errorEllipseConfidence: (value: LightningReportErrorEllipseConfidence) =>
				`reportsDisplayReportSettingsErrorEllipseConfidence-${value}`,
			showStrikeDamagePotential: 'reportsDisplayReportSettingsShowStrikeDamagePotential',
			strikeDamagePotentialOption: (value: StrikeDamagePotentialOption) => `reportsDisplayReportSettingsStrikeDamagePotential-${value}`,
			showCloudToCloud: 'reportsDisplayReportSettingsShowCloudToCloud',
			signalStrengthFilter: (type: 'all' | 'positive' | 'custom') => `reportsDisplayReportSettingsSignalStrengthSelect-${type}`,
			downloadReport: 'reportsDisplayReportDownload',
			manageSchedule: 'reportsDisplayReportManageSchedule',
		},
		displayResults: {
			view: (view: ReportView) => `reportsDisplayResultsView-${view}`,
			railView: (view: ReportView) => `reportsDisplayResultsRailView-${view}`,
		},
		displayDataTable: {
			root: 'reportsDisplayDataTable',
			pagination: {
				page: 'reportsDisplayDataTablePaginationPage',
				previous: 'reportsDisplayDataTablePaginationPrevious',
				next: 'reportsDisplayDataTablePaginationNext',
			},
		},
		table: {
			nameLocation: (id: string) => `reportsTableNameLocation-${id}`,
			allStatuses: (id: string) => `reportsTableAllStatuses-${id}`,
			timeRange: (id: string) => `reportsTableTimeRange-${id}`,
			parameterAndDataIncluded: (id: string) => `reportsTableParameterAndDataIncluded-${id}`,
			created: (id: string) => `reportsTableCreated-${id}`,
			schedule: {
				toggleExpanded: (id: string) => `reportsTableScheduleToggle-${id}`,
				menuBtn: (id: string) => `reportsTableScheduleMenuBtn-${id}`,
				loadMore: (scheduleId: string) => `reportsTableScheduleLoadMore-${scheduleId}`,
				loadMoreLabel: (scheduleId: string) => `reportsTableScheduleLoadMoreLabel-${scheduleId}`,
			},
			tabs: {
				tab: (tab: 'ALL' | 'REPEATING' | 'ANALYSIS') => `reportsTableTab-${tab}`,
			},
		},
		scheduleActionsMenu: {
			menuPause: 'reportsScheduleMenuPause',
			menuResume: 'reportsScheduleMenuResume',
			menuCancel: 'reportsScheduleMenuCancel',
		},
	},
	addMenu: {
		main: 'addMenuDropdownBtn',
		devices: 'addMenuDevices',
		assets: 'addMenuAssets',
		alerts: 'addMenuAlerts',
		user: 'addMenuUser',
		userList: 'addMenuUserList',
		analysisReport: 'addMenuAnalysisReport',
	},
	assets: {
		formDescription: 'assetFormDescription',
		formName: 'assetFormName',
		formType: 'assetFormType',
		formTypeMenuItem: 'assetFormTypeMenuItem',
		formGeometry: 'assetFormGeometry',
		formCoordinates: (geometryIndex: number, coordinateIndex: number) => {
			return {
				latitude: `assetFormLatitude-${geometryIndex}-${coordinateIndex}`,
				longitude: `assetFormLongitude-${geometryIndex}-${coordinateIndex}`,
			}
		},
		formLabel: (geometryIndex: number, coordinateIndex: number) => `assetFormCoordinateLabel-${geometryIndex}-${coordinateIndex}`,
		formDeleteAll: 'assetFormDeleteAll',
		formPowerCurve: 'assetFormPowerCurve',
		formMetaData: 'assetFormMetaData',
		formSearch: 'assetFormSearch',
		formMapBtnGroup: 'assetFormMapBtnGroup',
		formUploadFile: {
			btn: 'assetFormUploadFile',
			uploadBtn: 'uploadFileButton',
			uploadCancelBtn: 'uploadFileCancelButton',
			moreLessBtn: 'uploadFileMoreLessBtn',
			dragDropArea: 'uploadFileDragDropArea',
		},
	},
	alerts: {
		locationStep: {
			locations: 'alertsLocationStepLocation',
			locationFormHeader: 'alertsLocationFormHeader',
			search: 'alertsLocationSearch',
			searchXBtn: 'alertsLocationSearchXbtn',
		},
		parameterStep: {
			heading: 'alertsParameterHeading',
			parameters: 'alertsParameters',
		},
		conditionsStep: {
			hail: {
				realtime: 'alertsHailConditionsRealtime',
				bufferRadius: 'alertsHailConditionsBufferRadius',
				clearTime: 'alertsHailConditionsClearTime',
				probability: 'alertsHailConditionsProbability',
				horizonMinutes: 'alertsHailConditionsHorizonMinutes',
			},
			lightning: {
				alarm: 'alertsLightningConditionsAlarm',
				warning: 'alertsLightningConditionsWarning',
				info: 'alertsLightningConditionsInfo',
				info2: 'alertsLightningConditionsInfo2',
				advanceAlertTime: 'alertsLightningConditionsAdvanceAlertTime',
				advanceAlertTimeSwitch: 'alertsLightningConditionsAdvanceAlertTimeSwitch',
				addRing: 'alertsLightningConditionsAddRing',
			},
			wind: {
				horizon: 'alertsWindConditionsTimeHorizon',
				height: 'alertsWindConditionsHeight',
				speed: 'alertsWindConditionsSpeed',
				speedSwitch: 'alertsWindConditionsSpeedSwitch',
				gust: 'alertsWindConditionsGust',
				gustSwitch: 'alertsWindConditionsGustSwitch',
				and: 'alertsWindConditionsAND',
				or: 'alertsWindConditionsOR',
			},
			precipitation: {
				horizon: 'alertsPrecipitationConditionsTimeHorizon',
				pop: 'alertsPrecipitationConditionsPop',
				accum1hr: 'alertsPrecipitationConditionsAccum1hr',
				accum1hrSwitch: 'alertsPrecipitationConditionsAccum1hrSwitch',
				accum6hr: 'alertsPrecipitationConditionsAccum6hr',
				accum6hrSwitch: 'alertsPrecipitationConditionsAccum6hrSwitch',
				accum24hr: 'alertsPrecipitationConditionsAccum24hr',
				accum24hrSwitch: 'alertsPrecipitationConditionsAccum24hrSwitch',
				accum72hr: 'alertsPrecipitationConditionsAccum72hr',
				accum72hrSwitch: 'alertsPrecipitationConditionsAccum72hrSwitch',
			},
			measurement: {
				aboveBtn: 'alertsMeasurementConditionsAbove',
				belowBtn: 'alertsMeasurementConditionsBelow',
				threshold: 'alertsMeasurementConditionsThreshold',
				thresholdUnit: 'alertsMeasurementConditionsThresholdUnit',
			},
			weatherHazard: {
				parameter: (name: string) => `alertsWeatherHazardParameter-${name}`,
				parameterGrid: (severity: Severity | '-' | 'x', colName: string) => `alertsWeatherHazardParameter-${severity}-${colName}`,
			},
		},
		notificationsStep: {
			userList: {
				addUserList: 'alertsWindNotificationsAddUserList',
				userListSelect: (type?: 'alarm' | 'warning' | 'info' | 'info2' | 'forecast' | number) =>
					type ? `userListSelect-${type}` : 'userListSelect',
				usersListMenuItem: {
					userList: (name: string, index?: number) => `userListItem-${name}${index ? `-${index}` : ''}`,
					noUserListsSelected: 'userListItem-No user list notified',
				},
				clearUserList: 'clearUserList',
			},
			wind: {
				windLabel: 'alertsWindNotificationsWindLabel',
				frequency: 'alertsWindNotificationsFrequency',
				startTime: 'alertsWindNotificationsStartTime',
			},
			precipitation: {
				precipitationLabel: 'alertsPrecipitationNotificationsLabel',
			},
			measurement: {
				frequency: 'alertsNotificationsFrequency',
			},
		},
		guidelinesStep: {
			textArea: 'alertsGuidelinesStepTextArea',
		},
	},
	graph: {
		addAlert: 'graphAddAlert',
		deviceSelect: {
			main: 'graphDeviceSelect',
			option: (deviceId: string) => `graphDeviceSelectOption-${deviceId}`,
			alert: 'graphDeviceSelectAlert',
		},
		recharts: {
			root: 'graphRoot',
			item: (index: number) => `graphItem-${index}`,
			legendItem: (itemId: string) => `graphLegendItem-${itemId}`,
			tooltip: 'tooltip',
			tooltipDate: 'tooltipDate',
			alertLegend: 'graphAlertLegend',
			graphSurface: (index: number) => `graphSurface-${index}`,
		},
		infoRow: {
			main: 'graphTopInfoRow',
			timeAndDate: 'graphTopInfoRowTimeAndDate',
			measurements: {
				main: 'graphTopInfoRowMeasurements',
				value: 'graphTopInfoRowMeasurementValue',
				unit: 'graphTopInfoRowMeasurementUnit',
				name: 'graphTopInfoRowMeasurementName',
			},
		},
		range: { main: 'graphTimeRange', option: (o: string) => `graphTimeRangeOption-${o}` },
		xcastRange: { main: 'xcastGraphTimeRange', option: (o: string) => `xcastGraphTimeRangeOption-${o}` },
	},
	smp: {
		impersonate: { dropDown: 'impersonateDropDown', stopBtn: 'impersonateStopBtn', stopBtnBottom: 'impersonateStopBtnBottom' },
		webhooks: {
			form: {
				url: 'webhookFormUrl',
				eventTypes: 'webhookFormEventTypes',
				status: 'webhookFormStatus',
			},
			secretForm: {
				validFrom: 'webhookSecretFormValidFrom',
				validUntil: 'webhookSecretFormValidUntil',
			},
			webhookTable: {
				create: 'webhookTableCreate',
				edit: 'webhookTableEdit',
			},
			secretTable: {
				create: 'webhookSecretTableCreate',
				edit: 'webhookSecretTableEdit',
				showSecretValue: 'webhookSecretTableShowSecretValue',
			},
			invocationsTable: {
				showPayload: 'webhookInvocationTableShowPayload',
			},
			secretValueModal: {
				copySecretValue: 'webhookSecretValueModalCopySecretValue',
				close: 'webhookSecretValueModalClose',
			},
			invocationPayloadModal: {
				close: 'invocationPayloadModalClose',
			},
		},
		serviceMap: {
			deviceSearchInput: 'serviceMapDeviceSearchInput',
			searchBtn: 'serviceMapSearchBtn',
			searchCloseBtn: 'serviceMapSearchCloseBtn',
			topBtn: 'serviceMapTopBtn',
			table: 'serviceMapDeviceTable',
			compareCheckbox: (id: string) => `serviceMapCompareCheckbox-${id}`,
			filters: {
				activeCount: 'serviceMapFiltersActiveCount',
				toggleBtn: 'serviceMapFiltersToggleBtn',
				closeBtn: 'serviceMapFiltersCloseBtn',
				deviceInput: 'serviceMapFiltersDeviceInput',
				lastReadInput: 'serviceMapFiltersLastReadInput',
				operatorInput: 'serviceMapFiltersOperatorInput',
				subscriptionInput: 'serviceMapFiltersSubscriptionInput',
				customerInput: 'serviceMapFiltersCustomerInput',
				countryInput: 'serviceMapFiltersCountryInput',
				resetBtn: 'serviceMapFiltersResetBtn',
			},
		},
		serviceDetails: {
			status: 'serviceDetailsStatus',
			serial: 'serviceDetailsSerial',
			model: 'serviceDetailsModel',
			firmware: 'serviceDetailsFirmware',
			batteryLife: 'serviceDetailsBatteryLife',
			installDate: 'serviceDetailsInstallDate',
			diagnostics: {
				root: 'serviceDetailsDiagnostics',
			},
			lastOnline: 'serviceDetailsLastOnline',
			lastRead: 'serviceDetailsLastRead',
			location: {
				latitude: 'serviceDetailsLocationLatitude',
				longitude: 'serviceDetailsLocationLongitude',
			},
			deviceAndComponents: {
				device: (model?: string) => (model ? `serviceDetailsDevice-${model}` : 'serviceDetailsDevice'),
				component: (model?: string) => (model ? `serviceDetailsComponent-${model}` : 'serviceDetailsComponent'),
			},
			subscription: {
				detailsRoot: 'serviceDetailsSubscription',
				owner: 'serviceDetailsOwnerSubscription',
				status: 'serviceDetailsStatusSubscription',
				productName: 'serviceDetailsProductNameSubscription',
				startDate: 'serviceDetailsStartDateSubscription',
				endDate: 'serviceDetailsEndDateSubscription',
			},
			sim: {
				operator: 'serviceDetailsSimOperator',
				iccid: 'serviceDetailsSimIccid',
			},
			signal: {
				root: 'serviceDetailsSignal',
				topic: (id: string) => `serviceDetailsSignalTopic-${id}`,
				topicStrength: (id: string) => `serviceDetailsSignalTopicStrength-${id}`,
				topicRatio: (id: string) => `serviceDetailsSignalTopicRatio-${id}`,
				topicQuality: (id: string) => `serviceDetailsSignalTopicQuality-${id}`,
			},
		},
		deviceComparison: {
			table: 'deviceComparisonTable',
		},
		accounts: {
			root: 'accountsRoot',
			search: 'accountsSearchInput',
			userSearch: { root: 'accountsUserSearch', input: 'accountsUserSearchInput', btn: 'accountsUserSearchBtn' },
			accountRow: (accountName: string) => `accountsAccountRow-${accountName}`,
			accountDetails: {
				root: 'accountDetailsRoot',
				status: 'accountDetailsStatus',
				actions: {
					btn: 'accountDetailsActionsBtn',
					menuItem: (action: string) => `accountDetailsActionsMenuItem-${action}`,
				},
			},
			editAccountForm: {
				root: 'editAccountFormRoot',
				user: {
					name: 'editAccountFormName',
					email: 'editAccountFormEmail',
					phone: 'editAccountFormPhone',
				},
				subscription: {
					productOffering: {
						menu: 'editAccountFormProductOffering',
						menuItem: (offering: string) => `editAccountFormProductOfferingMenuItem-${offering}`,
					},
					stripeAccount: {
						menu: 'editAccountFormStripeAccount',
						menuItem: (account: string) => `editAccountFormStripeAccountMenuItem-${account}`,
					},
					stripeSubscriptionId: 'editAccountFormStripeSubscriptionId',
					mapLayers: 'editAccountFormMapLayers',
					numberInput: (name: string) => `editAccountFormNumberInput-${name}`,
					analyze: {
						menu: 'editAccountFormAnalyze',
						menuItem: (item: string) => `editAccountFormAnalyzeMenuItem-${item}`,
					},
					toggles: (name: string) => `editAccountFormToggle-${name}`,
				},
				usersCount: 'editAccountFormUsersCount',
			},
		},
	},
	impacts: {
		periodDay: 'impactsPeriodDay',
		periodWeek: 'impactsPeriodWeek',
		timezoneUser: 'impactsTimezoneUser',
		timezoneAsset: 'impactsTimezoneAsset',
		downloadBtn: 'impactsDownloadBtn',
		lastRefreshed: 'impactsLastRefreshed',
		dashboardTab: (tabId: string) => `impactsDashboardTab-${tabId}`,
		searchInput: 'impactsSearchInput',
		riskChip: (level: string) => `impactsRiskChip-${level}`,
		parameterSelect: 'impactsParameterSelect',
		parameterSelectAll: 'impactsParameterSelectAll',
		parameterMenuItem: (parameterId: string) => `impactsParameterMenuItem-${parameterId}`,
		sortSlot: (index: number) => `impactsSortSlot-${index}`,
		nowMarker: 'impactsNowMarker',
		swimlaneRow: (assetId: string) => `impactsSwimlaneRow-${assetId}`,
		scoresUnavailable: (assetId: string) => `impactsScoresUnavailable-${assetId}`,
		hazardRow: (hazardId: string) => `impactsHazardRow-${hazardId}`,
		favoriteBtn: (assetId: string) => `impactsFavoriteBtn-${assetId}`,
		graph: 'impactsGraph',
		table: 'impactsTable',
		forecastSummary: 'impactsForecastSummary',
		printTemplate: 'impactsPrintTemplate',
		assetSelect: {
			main: 'impactsAssetSelect',
			option: (assetId: string) => `impactsAssetSelectOption-${assetId}`,
		},
	},
	analyze: {
		noReports: 'noReports',
		lightningAnalysisBtn: 'lightningAnalysisBtn',
	},
	signalStrengthSlider: {
		slider: 'signalStrengthSlider',
		invertBtn: 'signalStrengthSliderInvertBtn',
	},
	mobile: { locationList: 'mobileLocationList' },
}
